import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';
import { useScrollReveal } from '../hooks/useScrollReveal';

export default function ProcessPage() {
  const { lang } = useLang();
  const tr = t[lang];
  const pTr = tr.processPage;
  const [refSteps, visibleSteps] = useScrollReveal();
  const [refGet, visibleGet] = useScrollReveal();

  return (
    <main data-testid="process-page">
      {/* Hero */}
      <section className="pt-32 pb-16 relative" style={{ backgroundColor: '#0D1F1F' }}>
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 70% 30%, rgba(155,89,245,0.4) 0%, transparent 50%)' }}
        />
        <div className="section-container relative text-center max-w-3xl mx-auto">
          <h1 className="font-sora font-black text-4xl sm:text-5xl text-white mb-3 animate-fadeInUp">
            {pTr.hero.title}
          </h1>
          <div className="w-14 h-0.5 gradient-bg rounded-full mx-auto mb-6" />
          <p className="text-gray-300 text-base md:text-lg font-inter animate-fadeInUp delay-100">
            {pTr.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-24" style={{ backgroundColor: '#0A1A1A' }}>
        <div className="section-container">
          <div ref={refSteps} className="space-y-8">
            {pTr.steps.map((step, i) => (
              <div
                key={i}
                data-testid={`process-step-detail-${i}`}
                className={`exevori-card p-8 md:p-10 reveal ${visibleSteps ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                  {/* Number */}
                  <div className="md:col-span-2 flex items-center gap-4">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center font-sora font-black text-2xl flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #4F8EF7, #9B59F5)' }}
                    >
                      {step.num}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="md:col-span-7">
                    <div className="gradient-text font-sora font-semibold text-xs uppercase tracking-wider mb-2">{step.subtitle}</div>
                    <h2 className="font-sora font-bold text-2xl text-white mb-4">{step.title}</h2>
                    <p className="text-gray-300 font-inter leading-relaxed">{step.desc}</p>
                  </div>

                  {/* Deliverables */}
                  <div className="md:col-span-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      {lang === 'fr' ? 'Livrables' : 'Deliverables'}
                    </div>
                    <div className="space-y-2">
                      {step.deliverables.map((d, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <FileText size={13} className="text-exevori-primary mt-0.5 flex-shrink-0" />
                          <span className="text-gray-400 text-sm font-inter">{d}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-24" style={{ backgroundColor: '#0D1F1F' }}>
        <div className="section-container">
          <div ref={refGet} className={`text-center mb-12 reveal ${visibleGet ? 'visible' : ''}`}>
            <h2 className="font-sora font-bold text-3xl text-white">{pTr.getWith.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {pTr.getWith.items.map((item, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 exevori-card p-5 reveal ${visibleGet ? 'visible' : ''}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <CheckCircle size={18} className="text-exevori-primary flex-shrink-0" />
                <span className="text-gray-300 text-sm font-inter">{item}</span>
              </div>
            ))}
          </div>
          <div className={`text-center mt-12 reveal ${visibleGet ? 'visible' : ''}`}>
            <Link to="/devis" data-testid="process-cta" className="btn-primary">
              {tr.nav.quote} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
